/**
 * AppShell - main app layout shell port z new-design/app.jsx (component App).
 *
 * Struktura HTML 1:1 z designu:
 *   <div class="app show-subnav">
 *     <RailNav /> <SubNav /> <div class="app__main"> <Topbar /> <div class="app__scroll"><Outlet/></div> </div> <MobileNav />
 *   </div>
 *
 * State (collapsed sidebar) + integracja z useRoute, useAuth, useCredits.
 */
import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { RailNav } from "./RailNav";
import { SubNav } from "./SubNav";
import { Topbar } from "./Topbar";
import { MobileNav } from "./MobileNav";
import { MobileSubTabs } from "./MobileSubTabs";
import { HelpWidget } from "@/components/HelpWidget";
import { useAuth } from "@/hooks/useAuth";
import { useCredits } from "@/hooks/useCredits";
import { Suspense } from "react";
import { useRoute } from "@/hooks/useRoute";
import { FeedbackInvitePrompt } from "@/components/FeedbackInvitePrompt";
import { FeedbackBanner } from "@/components/FeedbackBanner";
import { EmailVerificationBanner } from "@/components/EmailVerificationBanner";
import { useAdmin } from "@/hooks/useAdmin";
import { useCreditPurchase } from "@/hooks/useCreditPurchase";
import { toolById, navSection } from "@/lib/data";
import { getProjects, type Project } from "@/lib/apiClient";

function deriveTitle(routeName: string, toolId?: string, projectName?: string): string {
  if (routeName === "hub") return "Studio AI";
  if (routeName === "tool") return toolId ? toolById(toolId)?.name ?? "Narzędzie" : "Narzędzie";
  if (routeName === "gallery") return "Oferty";
  if (routeName === "photos") return "Galeria";
  if (routeName === "project") return projectName ?? "Projekt";
  if (routeName === "offer") return "Nowa oferta";
  if (routeName === "account") return "Konto";
  if (routeName === "agency") return "Agencja";
  if (routeName === "brand") return "Marka";
  if (routeName === "pricing") return "Cennik";
  if (routeName === "plans") return "Plany i kredyty";
  return "AdresFlow";
}

export function AppShell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { balance } = useCredits();
  const { route, sectionId, navigate: navRoute, goSection } = useRoute();
  const { isAdmin } = useAdmin();
  // Doładowania kredytów są na razie tylko dla adminów (patrz useCreditPurchase).
  const { visible: canBuyCredits } = useCreditPurchase();
  const [collapsed, setCollapsed] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // Gradient nad dolną belką: sygnał „jest jeszcze treść niżej". Gaśnie na końcu
  // strony, żeby nie przygaszać ostatniego przycisku (uwaga wspólnika 29.07:
  // belka „bardzo twardo" ucinała treść).
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  // Ankieta: otwierana z FeedbackBanner; auto-trigger po N generacjach działa dalej
  // wewnątrz FeedbackInvitePrompt (zewnętrzne `open` jest z nim OR-owane).
  const [surveyOpen, setSurveyOpen] = useState(false);

  const { data: projectsData } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const res = await getProjects();
      if ("error" in res) return { projects: [] as Project[] };
      return res.data!;
    },
    staleTime: 60_000,
    enabled: sectionId === "galeria" || sectionId === "zdjecia",
  });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 24 px tolerancji — subpikselowe zaokrąglenia na iOS potrafią zostawić
    // 1–2 px „reszty", przez którą gradient nigdy by nie zgasł.
    const update = () => setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 24);
    update();
    el.addEventListener("scroll", update, { passive: true });
    // Treść dosypuje się asynchronicznie (zapytania) — sam scroll nie wystarcza,
    // bo strona rośnie bez ruchu użytkownika.
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
    // Zmiana trasy podmienia korzeń strony w .app__scroll — trzeba obserwować nowy.
  }, [route.name, route.toolId, route.tab, route.filter, sectionId]);

  // NIE `?? 0` — `null` znaczy „saldo jeszcze nie dojechało", a nie „zero kredytów".
  // Licznik sam renderuje stan nieznany (patrz lib/credits).
  const credits = balance;
  const title = deriveTitle(route.name, route.toolId);
  // Na desktopie subnav już pokazuje nazwę sekcji — gdy tytuł w topbarze jest
  // dokładnie tym samym, chowamy go (patrz .topbar__title--dupe w CSS).
  const titleDupesSubnav = title === (navSection(sectionId)?.label ?? "");

  // Avatar initials z user email
  const initials = user?.email?.slice(0, 2).toUpperCase() ?? "??";
  const userName = user?.email ?? "—";

  const goPricing = () => navigate("/cennik");

  return (
    <div className={`app ${collapsed ? "is-collapsed" : "show-subnav"}`}>
      <RailNav
        sectionId={sectionId}
        onSection={goSection}
        credits={credits}
        onBuy={goPricing}
        canBuy={canBuyCredits}
        userInitials={initials}
        userName={userName}
        isAdmin={isAdmin}
      />
      <SubNav
        sectionId={sectionId}
        route={route}
        navigate={navRoute}
        onCollapse={() => setCollapsed(true)}
        credits={credits}
        projects={projectsData?.projects}
        onBuy={goPricing}
        canBuy={canBuyCredits}
      />
      <div className="app__main">
        {/* Belki globalne — wewnątrz .app__main (nie .app), żeby nie rozwalić gridu
            rail/subnav/main. EmailVerificationBanner ma priorytet: gdy e-mail nie jest
            potwierdzony, FeedbackBanner sam się chowa (nie dublujemy dwóch belek). */}
        <EmailVerificationBanner />
        <FeedbackBanner onOpen={() => setSurveyOpen(true)} />
        <Topbar
          title={title}
          titleDupe={titleDupesSubnav}
          collapsed={collapsed}
          onExpand={() => setCollapsed(false)}
          credits={credits}
          onBuy={goPricing}
          canBuy={canBuyCredits}
          onHelp={() => setHelpOpen(true)}
        />
        <MobileSubTabs sectionId={sectionId} route={route} navigate={navRoute} />
        <div className="app__scroll" ref={scrollRef}>
          <Suspense fallback={<div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}><div className="spinner-arc" /></div>}>
            <Outlet />
          </Suspense>
        </div>
        <div className={`mnav-fade ${atBottom ? "is-hidden" : ""}`} aria-hidden="true" />
      </div>

      <MobileNav sectionId={sectionId} onSection={goSection} onBuy={goPricing} onHelp={() => setHelpOpen(true)} />
      <HelpWidget open={helpOpen} onOpenChange={setHelpOpen} />
      <FeedbackInvitePrompt open={surveyOpen} onOpenChange={setSurveyOpen} />
    </div>
  );
}
